import { useFileDrop } from '@admin-platform/ui/components';
import classNames from 'classnames';
import {
  BookOpen,
  Check,
  Copy,
  CornerDownLeft,
  File as FileIcon,
  Paperclip,
  X,
} from 'lucide-react';
import type { ChangeEvent, ComponentType, FormEvent, ReactNode, SVGProps } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  AgentAttachmentMeta,
  AgentChoiceOption,
  OptionalAddition,
} from '../../agent/agent-api';
import { copyReply } from '../../agent/copy-reply';
import type { AgentAttachmentsController } from '../../agent/useAgentAttachments';
import { ATTACHMENT_ACCEPT, formatFileSize } from '../../agent/useAgentAttachments';
import type { AgentChatMessage, AgentProposal } from '../../agent/useAgentChat';
import { TOOL_LABELS } from '../../agent/useAgentChat';
import { Brynn } from '../../components/Brynn/Brynn';
import { Button } from '../../components/Button/Button';
import { Textarea } from '../../components/Inputs/Textarea';
import { AgentBlockView } from './AssistantBlocks';
import styles from './AssistantConversation.module.css';
import { CatalogArtifactCard } from './CatalogArtifactCard';
import { ProposalAdditions } from './ProposalAdditions';

/** A starter prompt shown as an empty-state card and as a composer chip. */
export interface Suggestion {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  subtitle: string;
  /** Short label for the in-conversation composer chips. */
  chip: string;
  prompt: string;
}

const PROPOSAL_STATUS_LABEL: Record<AgentProposal['status'], string> = {
  pending: 'Waiting for your confirmation',
  working: 'Running',
  confirmed: 'Confirmed',
  declined: 'Declined',
  superseded: 'Replaced by your new message',
  failed: 'Could not be resolved — try again',
  uncertain: 'Execution outcome is unknown. The write may have completed; do not retry it.',
};

const REMARK_PLUGINS = [remarkGfm];

/**
 * The message sent when the user picks an optional element to add from a create
 * card. Phrased so the agent gathers any value it still needs, then re-proposes
 * the same thing with that element included. The declined proposal is still in
 * the transcript, so "this" resolves to it.
 */
function buildRevisionInstruction(item: OptionalAddition): string {
  return `Before creating this, I would like to add the ${item.label.toLowerCase()} — ${item.hint} Ask me for whatever you need, then propose it again with that included.`;
}

/**
 * Assistant replies are model-generated markdown. react-markdown is safe by
 * default (raw HTML is not rendered, unsafe URL schemes are stripped). Links
 * open in the SAME tab — e.g. a record the agent just created navigates in
 * place. `node` must be dropped before it reaches the DOM element.
 */
const MARKDOWN_COMPONENTS: Components = {
  a({ node, ...props }) {
    // react-markdown passes the link text through `props.children`, which the
    // rule can't see behind the spread.
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a {...props} />;
  },
  // Wrap tables so they get a bordered, rounded, horizontally-scrollable
  // container matching the structured table blocks.
  table({ node, children, ...props }) {
    return (
      <div className={styles.TableScroll}>
        <table {...props}>{children}</table>
      </div>
    );
  },
};

/**
 * Renders a turn's body: assistant text as markdown, user/error text verbatim
 * (MessageBody keeps `white-space: pre-wrap` for those), and the streaming
 * placeholder while the first tokens are still arriving.
 */
function renderMessageBody(message: AgentChatMessage): ReactNode {
  if (message.content) {
    if (message.role === 'assistant' && !message.isError) {
      return (
        <div className={styles.Markdown}>
          <Markdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
            {message.content}
          </Markdown>
        </div>
      );
    }
    return message.content;
  }
  if (message.streaming && message.proposals.length === 0 && message.blocks.length === 0) {
    // Working indicator while the agent thinks / runs tools before any tokens.
    return (
      <span className={styles.Dots} role="status" aria-label="Working">
        <span className={styles.Dot} />
        <span className={styles.Dot} />
        <span className={styles.Dot} />
      </span>
    );
  }
  return null;
}

function ProposalDetailValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    return (
      <ul className={styles.ProposalNestedList}>
        {value.map((item, index) => (
          <li key={index} className={styles.ProposalNestedItem}>
            <ProposalDetailValue value={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (value !== null && typeof value === 'object') {
    return (
      <dl className={styles.ProposalNestedDetails}>
        {Object.entries(value as Record<string, unknown>).map(([key, nestedValue]) => (
          <div key={key} className={styles.ProposalNestedRow}>
            <dt className={styles.ProposalNestedKey}>{key}</dt>
            <dd className={styles.ProposalNestedValue}>
              <ProposalDetailValue value={nestedValue} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <>{value == null ? '—' : String(value)}</>;
}

export interface AssistantConversationProps {
  messages: AgentChatMessage[];
  isStreaming: boolean;
  send: (text: string, attachments?: AgentAttachmentMeta[]) => void | Promise<void>;
  respondToAction: (
    actionId: string,
    decision: 'confirm' | 'reject',
    reason?: string,
  ) => void | Promise<void>;
  /** Withdraw a proposal and ask the agent to re-propose it with an added element. */
  reviseProposal: (actionId: string, instruction: string) => void | Promise<void>;
  /** Starter prompts for the empty state. */
  suggestions: Suggestion[];
  /** Empty-state heading and lead copy. */
  emptyTitle: string;
  emptySubtitle: string;
  /**
   * Attachment state from `useAgentAttachments`. When provided, the composer
   * lets the user attach files (PDF, image, text) for the agent to read; the
   * caller owns the hook so this surface stays free of upload plumbing.
   */
  attachmentController?: AgentAttachmentsController;
  /**
   * Text to put in the composer, for a page that links here with a question
   * ready to ask. It is only placed in the box: the person still sends it, so
   * a link cannot make the agent do anything on its own.
   */
  initialDraft?: string;
  /**
   * Shown above the composer, inside the dock: a control that applies to what is sent next (who a
   * reply is for).
   */
  composerAccessory?: ReactNode;
  /** Buttons in the composer row beside the attach button (the "Your docs" switch). */
  composerTools?: ReactNode;
  /** Offer "Copy" under each settled answer, to paste it elsewhere (Ask Revvly, not onboarding). */
  copyableAnswers?: boolean;
  /**
   * `page` (default) for the full-page assistant and onboarding steps;
   * `panel` for the docked bubble, which is narrower: tighter padding, one
   * suggestion per row.
   */
  variant?: 'page' | 'panel';
}

/**
 * The shared Ask Revvly conversation surface: an empty state with starter
 * prompts, the streaming message thread (markdown prose, structured answer
 * cards, tool-activity chips, and write-action confirmation cards), and the
 * docked composer with quick-prompt chips. Owns only local render state (draft,
 * picks, scroll); the conversation itself is driven by the `useAgentChat` hook
 * passed in via props, so callers control session scope. Used by the full-page
 * assistant and the onboarding product-setup step.
 */
export function AssistantConversation({
  messages,
  isStreaming,
  send,
  respondToAction,
  reviseProposal,
  suggestions,
  emptyTitle,
  emptySubtitle,
  attachmentController,
  initialDraft,
  composerAccessory,
  composerTools,
  copyableAnswers = false,
  variant = 'page',
}: AssistantConversationProps) {
  const [draft, setDraft] = useState(initialDraft ?? '');
  const allowAttachments = attachmentController !== undefined;
  const attachments = attachmentController?.attachments ?? [];
  const failed = attachmentController?.failed ?? [];
  const isUploading = attachmentController?.isUploading ?? false;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Which option was picked for each inline choice block (keyed by choiceId),
  // so an answered picker renders its resolved chip.
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  // Which proposal (if any) is in "Add detail" picker mode, so its optional
  // additions render as a chooser instead of a static list.
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  /** The card whose Decline opened its optional "why not?" box, and what is typed in it. */
  const [declining, setDeclining] = useState<{ actionId: string; reason: string } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  /** The answer whose "Copy" just worked, by message index, so its button says so for a moment. */
  const [copied, setCopied] = useState<number | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  /** Each answer's rendered body, by message index, so "Copy" can put the HTML on the clipboard. */
  const bodyRefs = useRef(new Map<number, HTMLDivElement>());

  const copyAnswer = async (index: number, message: AgentChatMessage) => {
    const html = bodyRefs.current.get(index)?.innerHTML;
    if (!(await copyReply(message.content, html))) return;
    setCopied(index);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(null), 2000);
  };
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const hasConversation = messages.length > 0;

  useEffect(() => {
    if (!initialDraft) return;
    setDraft(initialDraft);
    inputRef.current?.focus();
  }, [initialDraft]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  // Auto-grow the composer to fit its content, so long drafts stay readable
  // while typing. Reset to `auto` before measuring so it also shrinks back
  // when text is deleted or the draft is cleared after sending; the CSS
  // max-height caps the growth and switches the box to scrolling past that.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Sending waits for every upload to settle, and a failed attachment must be
  // removed first — otherwise the message would silently go out without it.
  const attachmentsBlocked = isUploading || failed.length > 0;

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    // Read what the box shows, not only React state: some mobile keyboards
    // (dictation keyboards especially) put text in the field without an input
    // event React sees, which left the draft empty and the send dead.
    const text = (inputRef.current?.value ?? draft).trim();
    // A new message replaces any open proposal, so an open card never blocks the composer.
    if (!text || isStreaming || attachmentsBlocked) return;
    const ready: AgentAttachmentMeta[] = attachments.flatMap((att) =>
      att.fileId
        ? [
            {
              fileId: att.fileId,
              filename: att.filename,
              mimeType: att.mimeType,
              size: att.size,
              ...(att.previewUrl && { previewUrl: att.previewUrl }),
            },
          ]
        : [],
    );
    setDraft('');
    // The draft may already be '' (see above), so clear the field itself too.
    if (inputRef.current) inputRef.current.value = '';
    attachmentController?.clear();
    void send(text, ready.length > 0 ? ready : undefined);
  };

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) attachmentController?.addFiles(event.target.files);
    event.target.value = '';
  };

  // The whole conversation is the drop target, not just the composer — a file
  // dropped on the thread would otherwise open in the tab and leave the chat.
  const addDroppedFiles = useCallback(
    (files: File[]) => attachmentController?.addFiles(files),
    [attachmentController],
  );
  const { isDragging, bindings: dropBindings } = useFileDrop(
    addDroppedFiles,
    allowAttachments && !attachmentController?.isFull,
  );

  const handleChoice = (choiceId: string, option: AgentChoiceOption) => {
    if (isStreaming) return;
    setPicks((current) => ({ ...current, [choiceId]: [option.value] }));
    void send(option.label);
  };

  // A multiple-choice picker answers with every tick at once, as one message.
  const handleChoices = (choiceId: string, options: AgentChoiceOption[]) => {
    if (isStreaming || options.length === 0) return;
    setPicks((current) => ({ ...current, [choiceId]: options.map((option) => option.value) }));
    void send(options.map((option) => option.label).join(', '));
  };

  const composer = (
    <form
      className={classNames(styles.Composer, { [styles.ComposerDragging]: isDragging })}
      onSubmit={submit}
    >
      {attachments.length > 0 && (
        <div className={styles.Attachments}>
          {attachments.map((att) =>
            att.previewUrl && att.status !== 'error' ? (
              <div
                key={att.localId}
                className={classNames(styles.Thumb, {
                  [styles.ThumbUploading]: att.status === 'uploading',
                })}
                title={att.filename}
              >
                <img className={styles.ThumbImage} src={att.previewUrl} alt={att.filename} />
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className={styles.ThumbRemove}
                  aria-label={`Remove ${att.filename}`}
                  onClick={() => attachmentController?.remove(att.localId)}
                >
                  <X aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <div
                key={att.localId}
                className={classNames(styles.AttachmentChip, {
                  [styles.AttachmentChipError]: att.status === 'error',
                })}
                title={att.error ?? att.filename}
              >
                <FileIcon aria-hidden="true" />
                <span className={styles.AttachmentName}>{att.filename}</span>
                <span className={styles.AttachmentMeta}>
                  {att.status === 'uploading'
                    ? 'Uploading…'
                    : att.status === 'error'
                      ? 'Failed'
                      : formatFileSize(att.size)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className={styles.AttachmentRemove}
                  aria-label={`Remove ${att.filename}`}
                  onClick={() => attachmentController?.remove(att.localId)}
                >
                  <X aria-hidden="true" />
                </Button>
              </div>
            ),
          )}
        </div>
      )}
      {failed.length > 0 && (
        <p className={styles.AttachmentHint} role="alert">
          {failed[0].filename}: {failed[0].error ?? 'Upload failed.'} Remove it to send.
        </p>
      )}
      <div className={styles.ComposerRow}>
        {allowAttachments && (
          <>
            {/* No design-system file input exists; a hidden native picker is the
                established pattern (see InvoiceFileUpload). */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className={styles.AttachInput}
              onChange={onPickFiles}
              tabIndex={-1}
              aria-hidden="true"
            />
            <Button
              type="button"
              variant="ghost"
              className={styles.AttachButton}
              disabled={attachmentController.isFull}
              aria-label="Attach a file"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip aria-hidden="true" />
            </Button>
          </>
        )}
        {composerTools}
        <Textarea
          ref={inputRef}
          className={styles.Input}
          value={draft}
          rows={1}
          maxLength={4000}
          aria-label="Message the assistant"
          onChange={(event) => setDraft(event.target.value)}
          onCompositionEnd={(event) => setDraft(event.currentTarget.value)}
          onBlur={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button
          type="submit"
          variant="ghost"
          className={styles.Send}
          // Not gated on the draft: submit() ignores an empty box, and gating on
          // React state disabled the button whenever that state lagged the field.
          disabled={isStreaming || attachmentsBlocked}
          aria-label="Send message"
        >
          <CornerDownLeft aria-hidden="true" />
        </Button>
      </div>
    </form>
  );

  // Approve/Decline while pending (plus an "Add detail" toggle when the card has
  // optional elements), else a resolved-status line — shared by both the rich
  // artifact card and the plain detail fallback.
  const renderProposalFooter = (proposal: AgentProposal) => {
    if (proposal.status !== 'pending') {
      return (
        <p
          className={classNames(styles.ProposalStatus, {
            [styles.ProposalStatusError]: proposal.status === 'failed',
            [styles.ProposalStatusUncertain]: proposal.status === 'uncertain',
          })}
        >
          {proposal.statusMessage ?? PROPOSAL_STATUS_LABEL[proposal.status]}
        </p>
      );
    }
    const isEditing = editingActionId === proposal.actionId;
    const hasAdditions = !!proposal.optionalAdditions?.length;
    if (declining?.actionId === proposal.actionId) {
      const reasonId = `decline-reason-${proposal.actionId}`;
      return (
        <div className={styles.DeclineReason}>
          <label htmlFor={reasonId} className={styles.DeclineReasonLabel}>
            Why not? Optional: the assistant remembers it and will not suggest this again.
          </label>
          <Textarea
            id={reasonId}
            rows={2}
            autoGrow
            maxLength={500}
            value={declining.reason}
            onChange={(e) => setDeclining({ actionId: proposal.actionId, reason: e.target.value })}
            disabled={isStreaming}
          />
          <div className={styles.ProposalActions}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isStreaming}
              onClick={() => {
                const reason = declining.reason.trim();
                setDeclining(null);
                void respondToAction(proposal.actionId, 'reject', reason || undefined);
              }}
            >
              Decline
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isStreaming}
              onClick={() => setDeclining(null)}
            >
              Back
            </Button>
          </div>
        </div>
      );
    }
    return (
      <>
        {proposal.statusMessage && (
          <p className={classNames(styles.ProposalStatus, styles.ProposalStatusError)}>
            {proposal.statusMessage}
          </p>
        )}
        <div className={styles.ProposalActions}>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={isStreaming}
            onClick={() => void respondToAction(proposal.actionId, 'confirm')}
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isStreaming}
            onClick={() =>
              proposal.asksDeclineReason
                ? setDeclining({ actionId: proposal.actionId, reason: '' })
                : void respondToAction(proposal.actionId, 'reject')
            }
          >
            Decline
          </Button>
          {hasAdditions && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isStreaming}
              onClick={() =>
                setEditingActionId((current) =>
                  current === proposal.actionId ? null : proposal.actionId,
                )
              }
            >
              {isEditing ? 'Cancel' : 'Add detail'}
            </Button>
          )}
        </div>
      </>
    );
  };

  const renderProposal = (proposal: AgentProposal) => {
    // While the proposal is still on the table, surface the optional elements the
    // user hasn't set. "Add detail" turns them into a chooser; picking one
    // withdraws the proposal and asks the agent to re-propose it with that added.
    const additions =
      proposal.status === 'pending' && proposal.optionalAdditions?.length ? (
        <ProposalAdditions
          items={proposal.optionalAdditions}
          editing={editingActionId === proposal.actionId}
          disabled={isStreaming}
          onAdd={(item) => {
            setEditingActionId(null);
            void reviseProposal(proposal.actionId, buildRevisionInstruction(item));
          }}
        />
      ) : null;

    // Catalog creates (bundle / module / component) render as a rich artifact
    // card; everything else keeps the plain summary + detail-rows card.
    if (proposal.artifact) {
      return (
        <CatalogArtifactCard
          key={proposal.actionId}
          artifact={proposal.artifact}
          additions={additions}
          footer={renderProposalFooter(proposal)}
        />
      );
    }
    return (
      <div key={proposal.actionId} className={styles.ProposalCard}>
        <p className={styles.ProposalSummary}>{proposal.summary}</p>
        <dl className={styles.ProposalDetails}>
          {Object.entries(proposal.input).map(([key, value]) => (
            <div key={key} className={styles.ProposalDetailRow}>
              <dt className={styles.ProposalDetailKey}>{key}</dt>
              <dd className={styles.ProposalDetailValue}>
                <ProposalDetailValue value={value} />
              </dd>
            </div>
          ))}
        </dl>
        {additions}
        {renderProposalFooter(proposal)}
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.Conversation, variant === 'panel' && styles.ConversationPanel)}
      {...dropBindings}
    >
      {isDragging && (
        <div className={styles.DropOverlay} aria-hidden="true">
          <Paperclip aria-hidden="true" />
          <span>Drop files to attach</span>
        </div>
      )}
      <div className={styles.Scroll}>
        {hasConversation ? (
          <div className={styles.Thread} aria-live="polite">
            {messages.map((message, index) => {
              if (message.role === 'user') {
                return (
                  <div key={index} className={styles.UserTurn}>
                    {message.attachments && message.attachments.length > 0 && (
                      <div className={styles.UserAttachments}>
                        {message.attachments.some((att) => att.previewUrl) && (
                          <div className={styles.Stack}>
                            {message.attachments
                              .filter((att) => att.previewUrl)
                              .map((att) => (
                                <img
                                  key={att.fileId}
                                  className={styles.StackImage}
                                  src={att.previewUrl}
                                  alt={att.filename}
                                  title={att.filename}
                                />
                              ))}
                          </div>
                        )}
                        {message.attachments
                          .filter((att) => !att.previewUrl)
                          .map((att) => (
                            <span key={att.fileId} className={styles.UserAttachmentChip}>
                              <FileIcon aria-hidden="true" />
                              <span className={styles.AttachmentName}>{att.filename}</span>
                            </span>
                          ))}
                      </div>
                    )}
                    <div className={styles.MessageUser}>{message.content}</div>
                  </div>
                );
              }
              const body = renderMessageBody(message);
              return (
                <div key={index} className={styles.AiTurn}>
                  <span className={styles.AiAvatar}>
                    <Brynn />
                  </span>
                  <div className={styles.AiBody}>
                    {message.toolActivity.length > 0 && (
                      <div className={styles.ToolActivity}>
                        {message.toolActivity.map((tool, toolIndex) => (
                          <span
                            key={`${tool}-${toolIndex}`}
                            className={classNames(styles.ToolChip, {
                              [styles.ToolChipDocs]: tool === 'search_help_articles',
                            })}
                          >
                            {tool === 'search_help_articles' && <BookOpen aria-hidden="true" />}
                            {TOOL_LABELS[tool] ?? tool}
                          </span>
                        ))}
                      </div>
                    )}
                    {body && (
                      <div
                        ref={(element) => {
                          if (element) bodyRefs.current.set(index, element);
                          else bodyRefs.current.delete(index);
                        }}
                        className={classNames(styles.MessageBody, {
                          [styles.MessageError]: message.isError,
                        })}
                      >
                        {body}
                      </div>
                    )}
                    {copyableAnswers &&
                      message.content &&
                      !message.streaming &&
                      !message.isError && (
                        <div className={styles.AnswerActions}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            className={styles.CopyButton}
                            onClick={() => void copyAnswer(index, message)}
                          >
                            {copied === index ? (
                              <Check aria-hidden="true" />
                            ) : (
                              <Copy aria-hidden="true" />
                            )}
                            {copied === index
                              ? 'Copied'
                              : message.toolActivity.includes('search_help_articles')
                                ? 'Copy reply'
                                : 'Copy'}
                          </Button>
                        </div>
                      )}
                    {message.blocks.map((block, blockIndex) => (
                      <AgentBlockView
                        key={blockIndex}
                        block={block}
                        onChoice={handleChoice}
                        onChoices={handleChoices}
                        pickedValues={block.kind === 'choice' ? picks[block.choiceId] : undefined}
                        disabled={isStreaming}
                        stale={index !== messages.length - 1}
                      />
                    ))}
                    {message.proposals.map(renderProposal)}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        ) : (
          <div className={styles.Empty}>
            <span className={styles.EmptyMark}>
              <Brynn />
            </span>
            <h1 className={styles.EmptyTitle}>{emptyTitle}</h1>
            <p className={styles.EmptySubtitle}>{emptySubtitle}</p>
            <div className={styles.Suggestions}>
              {suggestions.map((suggestion) => (
                <Button
                  key={suggestion.title}
                  type="button"
                  variant="outline"
                  className={styles.SuggestionCard}
                  onClick={() => void send(suggestion.prompt)}
                >
                  <span className={styles.SuggestionIcon}>
                    <suggestion.icon aria-hidden="true" />
                  </span>
                  <span className={styles.SuggestionText}>
                    <span className={styles.SuggestionTitle}>{suggestion.title}</span>
                    <span className={styles.SuggestionSubtitle}>{suggestion.subtitle}</span>
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.ComposerDock}>
        {composerAccessory && <div className={styles.ComposerAccessory}>{composerAccessory}</div>}
        {composer}
      </div>
    </div>
  );
}

export default AssistantConversation;
